import { useState, useRef, useEffect } from 'react'
import { cn } from '../../utils'
import styles from '../../components/releases/Releases.module.css'

interface Tab { id: number; name: string }

interface PageTabBarProps {
  tabs: Tab[]
  activeTabId: number | null
  defaultLabel: string
  onTabClick: (id: number | null) => void
  onCreateTab: (name: string) => void
  onDeleteTab: (id: number) => void
}

export function PageTabBar({
  tabs,
  activeTabId,
  defaultLabel,
  onTabClick,
  onCreateTab,
  onDeleteTab,
}: PageTabBarProps) {
  const [showInput, setShowInput] = useState(false)
  const [newName, setNewName] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (showInput) inputRef.current?.focus()
  }, [showInput])

  const commit = () => {
    if (!newName.trim()) return
    onCreateTab(newName.trim())
    setNewName('')
    setShowInput(false)
  }

  const cancel = () => { setShowInput(false); setNewName('') }

  return (
    <div className={styles.tabBar}>
      <button
        className={cn(styles.tab, activeTabId === null && styles.tabActive)}
        onClick={() => onTabClick(null)}
      >
        {defaultLabel}
      </button>

      {tabs.map(t => (
        <button
          key={t.id}
          className={cn(styles.tab, activeTabId === t.id && styles.tabActive)}
          onClick={() => onTabClick(t.id)}
        >
          {t.name}
          <span
            className={styles.tabClose}
            onClick={(e) => { e.stopPropagation(); onDeleteTab(t.id) }}
          >
            <i className="bi bi-x" />
          </span>
        </button>
      ))}

      {showInput ? (
        <div className={styles.newTabInput}>
          <input
            ref={inputRef}
            type="text"
            placeholder="Tab name..."
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') commit()
              if (e.key === 'Escape') cancel()
            }}
          />
          <button onClick={commit} className={styles.newTabConfirm}><i className="bi bi-check" /></button>
          <button onClick={cancel} className={styles.newTabCancel}><i className="bi bi-x" /></button>
        </div>
      ) : (
        <button
          className={styles.tabAdd}
          onClick={() => setShowInput(true)}
          title="Save current view as a new tab"
          aria-label="Save current view as a new tab"
        >
          <i className="bi bi-plus-lg" />
        </button>
      )}
    </div>
  )
}
